import React, { useState, useEffect } from "react"

import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorSelector from "../shared/BackgroundColorSelector"
import ImagePickerV2 from "../shared/ImagePickerV2"

import EditorFooter from "./shared/EditorFooter"
import ServiceImageObjectFitField from "./shared/ServiceImageObjectFitField"

// ColorInput component (not currently used but kept for future use)
// const ColorInput = ({ label, value, onChange, className = "" }) => (
//   <div className={`space-y-1 ${className}`}>
//     <label className="block text-sm font-medium text-gray-700">{label}</label>
//     <div className="flex gap-2">
//       <input
//         type="color"
//         value={value || "#ffffff"}
//         onChange={(e) => onChange(e.target.value)}
//         className="h-8 w-12 cursor-pointer rounded border border-gray-light"
//       />
//       <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#ffffff" className="flex-1" />
//     </div>
//   </div>
// )

const FeaturedServicesEditor = ({ section, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview, practice, pages } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "Featured Services",
    body: section?.body || "",
    settings: section?.settings || {
      layout: "double_img_r", // double_img_r, double_img_l, highlights_2, highlights_3
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      selectedServiceIds: [],
      showCTA: true,
      ctaText: "Learn More",
      ctaAction: "page",
      ctaTarget: "",
      image1Url: "",
      image2Url: "",
      imageObjectFit: "cover"
    }
  })

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null)

  const { showToast } = useToast()

  useEffect(() => {
    if (section) {
      // Use section headline if it exists, otherwise default to "Featured Services"
      // Make sure we never show the layout string as a headline
      const defaultHeadline =
        section.layout === "featured_services" ? "Featured Services" : section.headline || "Featured Services"
      setFormData({
        headline: defaultHeadline,
        body: section.body || "",
        settings: {
          layout: "double_img_r",
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          selectedServiceIds: [],
          showCTA: true,
          ctaText: "Learn More",
          ctaAction: "page",
          ctaTarget: "",
          image1Url: "",
          image2Url: "",
          ...section.settings
        }
      })
    }
  }, [section])

  // Get available public services
  const availableServices = (practice?.services || []).filter((service) => service.public === true)

  // Get selected services in order
  const selectedServices = (formData.settings.selectedServiceIds || [])
    .map((id) => availableServices.find((service) => service.id === id))
    .filter(Boolean)

  // Get unselected services
  const unselectedServices = availableServices.filter(
    (service) => !(formData.settings.selectedServiceIds || []).includes(service.id)
  )

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const newSettings = { ...prev.settings, [key]: value }

      // Immediately update preview for important settings (avoids useEffect loops)
      const importantSettings = [
        "layout",
        "imageObjectFit",
        "showBackgroundColor",
        "backgroundColor",
        "selectedServiceIds",
        "title",
        "subtitle",
        "showCTA",
        "ctaText",
        "ctaAction",
        "ctaTarget",
        "image1Url",
        "image2Url"
      ]
      if (section?.id && importantSettings.includes(key)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...prev,
          settings: newSettings
        })
      }

      return {
        ...prev,
        settings: newSettings
      }
    })
  }

  // Service selection handlers
  const handleAddService = (serviceId) => {
    const currentIds = formData.settings.selectedServiceIds || []
    const newSelectedIds = [...currentIds, serviceId]
    handleSettingsChange("selectedServiceIds", newSelectedIds)
  }

  const handleRemoveService = (serviceId) => {
    const currentIds = formData.settings.selectedServiceIds || []
    const newSelectedIds = currentIds.filter((id) => id !== serviceId)
    handleSettingsChange("selectedServiceIds", newSelectedIds)
  }

  const handleSelectAllServices = () => {
    const allServiceIds = availableServices.map((s) => s.id)
    handleSettingsChange("selectedServiceIds", allServiceIds)
  }

  const handleClearServices = () => {
    handleSettingsChange("selectedServiceIds", [])
  }

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e, index) => {
    e?.preventDefault?.()
    if (draggedIndex === null || draggedIndex === index) return

    const currentIds = formData.settings.selectedServiceIds || []
    const newSelectedIds = [...currentIds]
    const draggedId = newSelectedIds[draggedIndex]
    newSelectedIds.splice(draggedIndex, 1)
    newSelectedIds.splice(index, 0, draggedId)

    handleSettingsChange("selectedServiceIds", newSelectedIds)
    setDraggedIndex(index)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  // Image select handlers - store imageUrl directly in settings
  const handleImage1Select = ({ imageUrl }) => {
    if (imageUrl) {
      handleSettingsChange("image1Url", imageUrl)
      showToast("Image selected successfully!")
    }
  }

  const handleImage2Select = ({ imageUrl }) => {
    if (imageUrl) {
      handleSettingsChange("image2Url", imageUrl)
      showToast("Image selected successfully!")
    }
  }

  const handleImage1Remove = () => {
    handleSettingsChange("image1Url", "")
    showToast("Image removed.")
  }

  const handleImage2Remove = () => {
    handleSettingsChange("image2Url", "")
    showToast("Image removed.")
  }

  const handleSubmit = () => {
    onSave({
      layout: "featured_services",
      ...formData
    })
  }

  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  // Layout options with preview images - updated to match Figma design
  const layoutOptions = [
    {
      id: "list",
      name: "List",
      description: "Simple list of services with title and CTA",
      image: require("../../../../../assets/images/builder/v2/layout-images/list.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/list-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/list-selected.png")
    },
    {
      id: "double_img_r",
      name: "Double Image Right",
      description: "Text on left, overlapping images on right",
      image: require("../../../../../assets/images/builder/v2/layout-images/double-img-r.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/double-img-r-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/double-img-r-selected.png")
    },
    {
      id: "double_img_l",
      name: "Double Image Left",
      description: "Overlapping images on left, text on right",
      image: require("../../../../../assets/images/builder/v2/layout-images/double-img-l.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/double-img-l-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/double-img-l-selected.png")
    },
    {
      id: "highlights_2",
      name: "2 Highlights",
      description: "Two highlighted services in a grid",
      image: require("../../../../../assets/images/builder/v2/layout-images/2-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-selected.png")
    },
    {
      id: "highlights_3",
      name: "3 Highlights",
      description: "Three highlighted services in a grid",
      image: require("../../../../../assets/images/builder/v2/layout-images/3-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-selected.png")
    }
  ]

  const LayoutOption = ({ option, isSelected, onSelect }) => {
    const [isHovered, setIsHovered] = useState(false)

    const getImageSource = () => {
      if (isSelected) {
        return option.imageSelected
      } else if (isHovered) {
        return option.imageHover
      } else {
        return option.image
      }
    }

    return (
      <div
        className="cursor-pointer transition-all"
        onClick={() => onSelect(option.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={option.name}>
        <img
          src={getImageSource()}
          alt={`${option.name} layout`}
          className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
          onError={(e) => {
            console.warn(`Failed to load image: ${e.target.src}`) // eslint-disable-line no-console
            e.target.style.display = "none"
            e.target.nextSibling.style.display = "block"
          }}
        />
        {/* Fallback placeholder */}
        <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
          <div className="text-xs text-gray-500">{option.name}</div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Layout Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Select a layout design for this section:</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 place-content-start items-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => (
              <LayoutOption
                key={option.id}
                option={option}
                isSelected={formData.settings.layout === option.id}
                onSelect={(layoutId) => handleSettingsChange("layout", layoutId)}
              />
            ))}
          </div>
        </div>

        <ServiceImageObjectFitField
          value={formData.settings.imageObjectFit || "cover"}
          onChange={(value) => handleSettingsChange("imageObjectFit", value)}
        />

        {/* Services Selection Section - Hidden for double image layouts */}
        {formData.settings.layout !== "double_img_r" && formData.settings.layout !== "double_img_l" && (
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
              <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
                <p className="block leading-[24px]">Services to Display</p>
              </div>
              <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
                <p className="block leading-[20px]">
                  Choose which services to display and drag to reorder them. Leave empty to show all services.
                </p>
              </div>
            </div>

            {/* Selected Services */}
            {selectedServices.length > 0 && (
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="flex items-center justify-between">
                  <div className="relative shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-builder-primary">
                    Selected Services ({selectedServices.length})
                  </div>
                  <button
                    type="button"
                    onClick={handleClearServices}
                    className="text-[13px] font-medium text-[#bd654f] hover:text-[#a55a45]">
                    Clear All
                  </button>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 rounded-lg border border-builder-border bg-[#f8fbfd] p-3">
                  {selectedServices.map((service, index) => (
                    <div
                      key={service.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`relative box-border flex w-full cursor-move flex-row content-stretch items-center justify-between gap-3 rounded-lg border bg-white p-3 transition-all hover:shadow-sm ${
                        draggedIndex === index ? "border-builder-accent opacity-50" : "border-builder-border-light"
                      }`}>
                      <div className="flex flex-1 items-center gap-3">
                        <div className="flex size-6 items-center justify-center">
                          <svg
                            className="size-4 text-builder-secondary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                          </svg>
                        </div>
                        <div className="flex flex-1 flex-col gap-1">
                          <div className="font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-builder-primary">
                            {service.name}
                          </div>
                          {service.amountCents > 0 && (
                            <div className="font-['Manrope:Regular',_sans-serif] text-[12px] text-builder-secondary">
                              ${(service.amountCents / 100).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveService(service.id)}
                        className="flex size-6 items-center justify-center rounded text-[#bd654f] hover:bg-red-50">
                        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Services */}
            {unselectedServices.length > 0 && (
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="flex items-center justify-between">
                  <div className="relative shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-builder-primary">
                    Available Services ({unselectedServices.length})
                  </div>
                  <button
                    type="button"
                    onClick={handleSelectAllServices}
                    className="text-[13px] font-medium text-builder-accent hover:text-builder-accent-hover">
                    Select All
                  </button>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 rounded-lg border border-builder-border bg-white p-3">
                  {unselectedServices.map((service) => (
                    <div
                      key={service.id}
                      className="relative box-border flex w-full flex-row content-stretch items-center justify-between gap-3 rounded-lg border border-builder-border-light bg-[#f8fbfd] p-3 transition-all hover:border-builder-border hover:bg-white">
                      <div className="flex flex-1 flex-col gap-1">
                        <div className="font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-builder-primary">
                          {service.name}
                        </div>
                        {service.amountCents > 0 && (
                          <div className="font-['Manrope:Regular',_sans-serif] text-[12px] text-builder-secondary">
                            ${(service.amountCents / 100).toFixed(2)}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAddService(service.id)}
                        className="flex h-8 items-center justify-center rounded bg-builder-accent px-3 text-[13px] font-medium text-white transition-colors hover:bg-builder-accent-hover">
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No services available message */}
            {availableServices.length === 0 && (
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-center justify-center gap-2 rounded-lg border border-builder-border bg-[#f8fbfd] p-6 text-center">
                <div className="font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-builder-secondary">
                  No public services available
                </div>
                <div className="font-['Manrope:Regular',_sans-serif] text-[13px] text-builder-secondary">
                  Create and publish services to display them here.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Images Section - Only for double image layouts */}
        {(formData.settings.layout === "double_img_r" || formData.settings.layout === "double_img_l") && (
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
              <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
                <p className="block leading-[24px]">Images</p>
              </div>
              <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
                <p className="block leading-[20px]">
                  Select images for the double image layout. These images will be displayed in the two image spaces.
                </p>
              </div>
            </div>

            {/* Image 1 */}
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-primary">
                <p className="block whitespace-pre leading-[20px]">Image 1</p>
              </div>
              {formData.settings.image1Url ? (
                <div className="mb-6 flex items-center justify-center">
                  <div className="flex items-center gap-4">
                    <img src={formData.settings.image1Url} alt="Image 1" className="size-16 rounded-lg object-cover" />
                    <div className="flex flex-col gap-2">
                      <ImagePickerV2
                        practice={practice}
                        onImageSelect={handleImage1Select}
                        fetching={false}
                        showCurateTab={true}
                        currentImageUrl={formData.settings.image1Url}
                        trigger={<button className="font-bold text-builder-accent hover:underline">Edit image</button>}
                      />
                      <button className="font-bold text-red-500 hover:underline" onClick={handleImage1Remove}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <ImagePickerV2
                  practice={practice}
                  onImageSelect={handleImage1Select}
                  fetching={false}
                  showCurateTab={true}
                  currentImageUrl={formData.settings.image1Url}
                  trigger={
                    <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-builder-border bg-white p-6 transition-colors hover:border-builder-accent">
                      <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-builder-bg-light">
                        <svg
                          className="size-5 text-builder-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                      <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-accent">
                        <p className="block whitespace-pre leading-[20px]">Upload Image 1</p>
                      </div>
                    </div>
                  }
                />
              )}
            </div>

            {/* Image 2 */}
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-primary">
                <p className="block whitespace-pre leading-[20px]">Image 2</p>
              </div>
              {formData.settings.image2Url ? (
                <div className="mb-6 flex items-center justify-center">
                  <div className="flex items-center gap-4">
                    <img src={formData.settings.image2Url} alt="Image 2" className="size-16 rounded-lg object-cover" />
                    <div className="flex flex-col gap-2">
                      <ImagePickerV2
                        practice={practice}
                        onImageSelect={handleImage2Select}
                        fetching={false}
                        showCurateTab={true}
                        currentImageUrl={formData.settings.image2Url}
                        trigger={<button className="font-bold text-builder-accent hover:underline">Edit image</button>}
                      />
                      <button className="font-bold text-red-500 hover:underline" onClick={handleImage2Remove}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <ImagePickerV2
                  practice={practice}
                  onImageSelect={handleImage2Select}
                  fetching={false}
                  showCurateTab={true}
                  currentImageUrl={formData.settings.image2Url}
                  trigger={
                    <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-builder-border bg-white p-6 transition-colors hover:border-builder-accent">
                      <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-builder-bg-light">
                        <svg
                          className="size-5 text-builder-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                      <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-accent">
                        <p className="block whitespace-pre leading-[20px]">Upload Image 2</p>
                      </div>
                    </div>
                  }
                />
              )}
            </div>
          </div>
        )}

        {/* Title and Subtitle Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Section Content</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Customize the title and subtitle for this section</p>
            </div>
          </div>

          {/* Title Input */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
            <label className="relative shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-builder-primary">
              Title
            </label>
            <input
              type="text"
              value={formData.settings.title || ""}
              onChange={(e) => handleSettingsChange("title", e.target.value)}
              placeholder="Featured Services"
              className="w-full rounded-lg border border-builder-border bg-white px-4 py-3 font-['Manrope:Regular',_sans-serif] text-[14px] text-builder-primary transition-colors focus:border-builder-accent focus:outline-none"
            />
          </div>

          {/* Subtitle Input */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
            <label className="relative shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold text-builder-primary">
              Subtitle (Optional)
            </label>
            <textarea
              value={formData.settings.subtitle || ""}
              onChange={(e) => handleSettingsChange("subtitle", e.target.value)}
              placeholder="Add a subtitle to describe your services..."
              rows={3}
              className="w-full rounded-lg border border-builder-border bg-white px-4 py-3 font-['Manrope:Regular',_sans-serif] text-[14px] text-builder-primary transition-colors focus:border-builder-accent focus:outline-none"
            />
          </div>
        </div>

        {/* CTA Section - Only for double image layouts */}
        {(formData.settings.layout === "double_img_r" || formData.settings.layout === "double_img_l") && (
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
              <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
                <p className="block leading-[24px]">Call-to-action button</p>
              </div>
              <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
                <p className="block leading-[20px]">
                  Guide visitors to the next step with a clear call-to-action button.
                </p>
              </div>
            </div>

            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
              <div className="relative box-border flex shrink-0 content-center items-center justify-start gap-3 p-0 [flex-flow:wrap]">
                <input
                  type="radio"
                  name="ctaAction"
                  value="page"
                  checked={formData.settings.ctaAction === "page"}
                  onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Page</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="ctaAction"
                  value="service"
                  checked={formData.settings.ctaAction === "service"}
                  onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Service/Package/Event</p>
                </div>
              </div>
            </div>

            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-start justify-start gap-1 p-0">
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[20px]">Button text</p>
                </div>
              </div>
              <div className="relative w-full shrink-0 rounded-lg bg-white">
                <input
                  type="text"
                  value={formData.settings.ctaText || "Learn More"}
                  onChange={(e) => handleSettingsChange("ctaText", e.target.value)}
                  placeholder="Learn More"
                  className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
                />
              </div>
            </div>

            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-start justify-start gap-1 p-0">
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[20px]">
                    {formData.settings.ctaAction === "page" ? "Page" : "Service/Package/Event"}
                  </p>
                </div>
              </div>
              <div className="relative w-full shrink-0 rounded-lg bg-white">
                <select
                  value={formData.settings.ctaTarget || ""}
                  onChange={(e) => handleSettingsChange("ctaTarget", e.target.value)}
                  className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none">
                  <option value="">Select</option>
                  {formData.settings.ctaAction === "page" ? (
                    pages
                      ?.filter((builderPage) => builderPage.defaultPageType !== "home")
                      ?.map((builderPage) => (
                        <option key={builderPage.id || builderPage.slug} value={builderPage.slug}>
                          {builderPage.title}
                        </option>
                      ))
                  ) : (
                    <>
                      {practice?.services
                        ?.filter((service) => service.public === true)
                        .map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                      {(!practice?.services || practice.services.filter((s) => s.public === true).length === 0) && (
                        <>
                          <option value="discovery-call">Discovery Call</option>
                          <option value="consultation">Consultation</option>
                        </>
                      )}
                    </>
                  )}
                </select>
              </div>
              {formData.settings.ctaAction === "service" && (
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-builder-secondary">
                  <p className="block whitespace-pre leading-[19.5px]">
                    Go to your <span className="cursor-pointer text-builder-accent">Services</span> to add additional
                    options.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Background Color Section */}
        <BackgroundColorSelector
          formData={formData}
          onSettingsChange={handleSettingsChange}
          sectionType="featured_services"
          theme={practice?.theme}
        />
      </div>

      {/* Footer Buttons */}
      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />
    </>
  )
}

export default FeaturedServicesEditor
